import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import css from './MainFiltersForm.css';

const getWeekdays = locale => {
  const df = new Intl.DateTimeFormat(locale, { weekday: 'long' });
  const baseDate = new Date(2026, 3, 6); // Poniedziałek

  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(baseDate);
    date.setDate(baseDate.getDate() + i);

    const fullDayName = df.format(date);
    return fullDayName.substring(0, 3);
  });
};

const CalendarSelector = ({ startDate, endDate, onSelect }) => {
  const intl = useIntl();
  const lang = intl?.locale || 'pl';

  const [viewDate, setViewDate] = useState(() => new Date());
  const [hoverDate, setHoverDate] = useState(null);

  // 1. Definiujemy "dzisiaj" z wyzerowanym czasem, aby porównywać tylko rok-miesiąc-dzień
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const monthName = useMemo(() => {
    return new Intl.DateTimeFormat(lang, {
      month: 'long',
      year: 'numeric',
    }).format(viewDate);
  }, [viewDate, lang]);

  const weekdays = useMemo(() => getWeekdays(lang), [lang]);

  const isSameDay = (d1, d2) => {
    return (
      d1 instanceof Date &&
      d2 instanceof Date &&
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  const calendarData = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0).getDate();

    let firstDayIdx = (firstDay.getDay() + 6) % 7;

    const days = [];
    for (let i = 0; i < firstDayIdx; i++) days.push(null);
    for (let d = 1; d <= lastDay; d++) days.push(new Date(year, month, d));
    return days;
  }, [viewDate]);

  const handleDateClick = date => {
    if (!date) return;

    // 2. Blokada kliknięcia: jeśli data jest mniejsza niż dzisiaj, ignorujemy akcję
    if (date < today) return;

    if (!startDate || (startDate && endDate)) {
      onSelect(date, null);
    } else if (date < startDate) {
      onSelect(date, null);
    } else {
      onSelect(startDate, date);
    }
  };

  const changeMonth = (offset, e) => {
    e.stopPropagation();
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + offset, 1));
  };

  return (
    <div
      className={classNames(css.dropdownBase, css.calendarDropdown)}
      onClick={e => e.stopPropagation()}
    >
      <div className={css.calendarHeader}>
        <button type="button" className={css.monthBtn} onClick={e => changeMonth(-1, e)}>
          &lt;
        </button>
        <div className={css.monthTitle} style={{ textTransform: 'capitalize' }}>
          {monthName}
        </div>
        <button type="button" className={css.monthBtn} onClick={e => changeMonth(1, e)}>
          &gt;
        </button>
      </div>

      <div className={css.calendarGrid} onMouseLeave={() => setHoverDate(null)}>
        {weekdays.map((d, idx) => (
          <span key={`${d}-${idx}`} className={css.weekday} style={{ textTransform: 'capitalize' }}>
            {d.replace('.', '')}
          </span>
        ))}

        {calendarData.map((date, i) => {
          if (!date) return <div key={`empty-${i}`} className={css.emptyDay} />;

          // 3. Sprawdzamy, czy dany dzień jest z przeszłości
          const isPast = date < today;

          const isStart = isSameDay(date, startDate);
          const isEnd = isSameDay(date, endDate);
          const inRange = startDate && endDate && date > startDate && date < endDate;
          // Hover aktywujemy tylko, jeśli najechany dzień nie jest z przeszłości
          const inHover =
            startDate &&
            !endDate &&
            hoverDate &&
            hoverDate >= today &&
            date > startDate &&
            date <= hoverDate;
          const isSunday = date.getDay() === 0;

          return (
            <div
              key={i}
              className={classNames(css.calendarDay, {
                [css.selectedStart]: isStart,
                [css.selectedEnd]: isEnd,
                [css.inRange]: inRange || inHover,
                [css.sunday]: isSunday && !isPast, // Niedziela świeci na czerwono tylko, jeśli jest przyszła
                [css.disabledDay]: isPast, // Nowa klasa dla dni przeszłych
              })}
              onClick={() => handleDateClick(date)}
              // Nie ustawiamy hoverDate, jeśli dzień jest przeszły
              onMouseEnter={() => !isPast && setHoverDate(date)}
            >
              <span className={css.dayNumber}>{date.getDate()}</span>
            </div>
          );
        })}
      </div>

      <div className={css.calendarDisclaimer}>
        <p className={css.disclaimerText}>
          <FormattedMessage id="MainFiltersForm.disclaimers" />
        </p>
      </div>
    </div>
  );
};

export default CalendarSelector;
